import { Component, Input } from '@angular/core';
import { BaseValueFieldComponent, FormControlInput, SimpleControlFieldComponent } from '@lib/modules/value-field/base';
import { Nullable, Optional } from '@lib/interfaces';
import { Subscription } from 'rxjs';

type ValueFieldType = string | number | boolean;
type ValueFieldLiteralType = 'text' | 'number' | 'email' | 'password' | 'date';

@Component({
    selector: 'app-value-field-input',
    templateUrl: './value-field-input.component.html',
    styleUrls: ['./value-field-input.component.scss'],
    providers: [{ provide: BaseValueFieldComponent, useExisting: ValueFieldInputComponent }],
})
export class ValueFieldInputComponent<T extends ValueFieldType> extends SimpleControlFieldComponent<T> {
    @Input() public inputType: ValueFieldLiteralType = 'text';
    @Input() public placeholder = '';
    @Input() public step = 1;
    @Input() public min = -Infinity;
    @Input() public max = Infinity;
    @Input() public minLength = 0;
    @Input() public maxLength = Infinity;
    @Input() public minDate: string | null = null;
    @Input() public maxDate: string | null = null;
    @Input() public isReadOnly = false;
    @Input() public disableTextEntryForDate = false; //Disable text entry for date input

    protected displayValue: Optional<Nullable<T>> = null;

    private _isUserInput = false;
    private _controlSub?: Subscription;

    public override get control(): FormControlInput<T> {
        return super.control;
    }

    @Input({ required: true })
    public override set control(control: FormControlInput<T>) {
        super.control = control;
        this._controlSub?.unsubscribe();
        this.displayValue = control.value;
        this._controlSub = control.valueChanges.subscribe(value => {
            if (!this._isUserInput) {
                this.displayValue = value;
            }
        });
        this.addSubscription(this._controlSub);
    }

    protected get placeholderText(): string {
        if (this.placeholder) return this.placeholder;

        return this.controlDisplayName;
    }

    protected setControlValue(event: Event): void {
        const inputElement: HTMLInputElement = event.target as HTMLInputElement;
        let inputValue: string | number = inputElement.value;

        if (this.inputType === 'number') {
            const parsed = parseFloat(inputValue);
            // Skip setValue when the numeric value hasn't changed to preserve cursor position
            if (!isNaN(parsed) && parsed === this.control.value) return;
            inputValue = parsed;
        }

        this._isUserInput = true;

        if (this.inputType === 'date') {
            if (typeof inputValue === 'string' && inputValue.length >= 5) {
                const year = inputValue.split('-')[0];
                if (year.length > 4) {
                    this.control.setErrors({ dateYearError: true });
                    this._isUserInput = false;
                    return;
                } else {
                    this.control.setValue(inputValue as Optional<Nullable<T>>);
                    this.control.markAsDirty();
                }
            }
        } else {
            this.control.setValue(inputValue as Optional<Nullable<T>>);
            this.control.markAsDirty();
        }

        this._isUserInput = false;
    }

    protected onKeyDown(event: KeyboardEvent): void {
        if (this.inputType === 'date' && this.disableTextEntryForDate) {
            const allowedKeys = [
                'Tab',
                'Shift',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Home',
                'End',
                'Escape',
                'Enter'
            ];
            if (!allowedKeys.includes(event.key)) {
                event.preventDefault();
            }
        }
    }

    protected onPasteOrDrop(event: Event): void {
        if (this.inputType === 'date' && this.disableTextEntryForDate) {
            event.preventDefault();
        }
    }

}
